import React, { useState, useEffect } from 'react';
import { useUserContext } from '../../../context/user_context/UserContext';
import { Loading } from '../../../components/loading/Loading';
import { Stack } from 'react-bootstrap';
import AppSidebar from '../../../components/navbar/DesktopAppSidebar';
import { StringKeys } from '../../../resources/strings/StringKeys';
import { useTranslation } from 'react-i18next';
import { Button, Grid, Typography } from '@mui/material';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { CloudFunctions } from '../../../utils/enums/CloudFunctions';
import { useToast, ToastType } from '../../../context/toast/ToastContext';
import { useLoadingContext } from '../../../context/loading/LoadingContext';
import { useOrganizationContext } from '../../../context/organization_context/OrganizationContext';
import usePermissions from '../../../utils/permissions/UsePermissions';
import { Modules } from '../../../utils/permissions/Modules';
import { PermissionTypes } from '../../../utils/permissions/PermissionTypes';
import { useSubscriptionContext } from '../../../context/subscription_context/SubscriptionContext';

function InactiveSubscription() {
    // Inputs
    const { firebaseUser } = useUserContext();
    const { t } = useTranslation();
    const { showToast } = useToast();
    const { setLoadingData } = useLoadingContext();
    const [customerId, setCustomerId] = useState('');
    const { organizationStripeInfo } = useOrganizationContext();
    const { hasPermission } = usePermissions();
    const { subscription } = useSubscriptionContext();

    useEffect(() => {
        if (hasPermission(Modules.SUBSCRIPTION, PermissionTypes.WRITE) && organizationStripeInfo) {
            setCustomerId(organizationStripeInfo.stripe_customer_id);
        }
    }, [organizationStripeInfo]);

    const redirectToCustomerPortal = async () => {
        setLoadingData(true)
        try {
            const functions = getFunctions();
            const createCustomerPortalSession = httpsCallable(functions, CloudFunctions.CreateCustomerPortalSession);
            const response = await createCustomerPortalSession({ customerId });

            if (response.data.url) {
                window.location.href = response.data.url;
            } else {
                showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER)
            }
        } catch (error) {
            showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER)
        } finally {
            setLoadingData(false)
        }
    };

    const renderContent = () => {
        if (hasPermission(Modules.SUBSCRIPTION, PermissionTypes.WRITE)) {
            const isSubscriptionCanceled = subscription?.status === 'canceled';
            return (
                <Stack direction='vertical' alignItems="center" gap={4}>
                    {isSubscriptionCanceled ? (
                        <>
                            <Typography variant="h5" textAlign="center" gutterBottom>
                                {t(StringKeys.PLEASE_REACTIVATE_YOUR_SUBSCRIPTION_TO_CONTINUE)}
                            </Typography>
                            <Grid container justifyContent="center">
                                <script async src="https://js.stripe.com/v3/pricing-table.js"></script>
                                <stripe-pricing-table 
                                    pricing-table-id={process.env.REACT_APP_STRIPE_REACTIVATION_PRICING_TABLE_ID}
                                    publishable-key={process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY}
                                    client-reference-id={firebaseUser.organization}
                                    customer-email={firebaseUser.email}
                                />
                            </Grid>
                        </>
                    ) : (
                        <Stack direction='horizontal' alignItems="center" gap={3}>
                            <Typography variant="h6" color="text.secondary">
                                {t(StringKeys.YOUR_SUBSCRIPTION_IS_INACTIVE)}
                            </Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={redirectToCustomerPortal}
                            >
                                {t(StringKeys.MANAGE_SUBSCRIPTION)}
                            </Button>
                        </Stack>
                    )}
                </Stack>
            );
        } else {
            return (
                <Stack direction='horizontal' gap={3}>
                    <Typography variant="body1" color="text.secondary">
                        {t(StringKeys.PLEASE_CONTACT_YOUR_ADMINISTRATOR_TO_REACTIVATE_YOUR_SUBSCRIPTION)}
                    </Typography>
                </Stack>
            );
        }
    };

    return (
        <>
            {firebaseUser ? (
                <AppSidebar
                    content={
                        <Stack gap={4}>
                            {renderContent()}
                        </Stack>
                    }
                />
            ) : (
                <AppSidebar
                    content={
                        <Loading />
                    }
                />
            )}
        </>
    );
}

export default InactiveSubscription;
